/**
 * TK-44 §5 (lee 24/10, mục 4/5) — XẾP VỊ TRÍ ĐÈN trong Cài đặt ban đầu (`/cai-dat-ban-dau?tu=<id>&xep=1`), bố cục an toàn iPad 10".
 *   Từ trên xuống, chiếm trọn chiều cao màn (100dvh trừ thanh đầu + thanh đáy), KHÔNG cuộn trang:
 *     1. Thanh trên một hàng: tên tủ · ← Về bảng node · Về tủ · tìm đèn · x / y đã đặt · Đánh số CS01… · Lưu · Hủy.
 *     2. Dải thẻ đèn cuộn ngang (cao cố định): Tên · ID gốc · ID liên kết · Tìm được lúc · Trạng thái · gps_local · gps_map · Loại bỏ.
 *     3. Bản đồ phần còn lại: CHỈ tủ đang cài + đèn của nó, nền TỐI; chú giải đếm đèn của tủ này.
 *   Đặt đèn: chạm thẻ để chọn · chạm bản đồ để đặt · kéo chấm để chỉnh · chạm thẻ đèn đã đặt ⇒ bay tới đèn.
 *   TK-45 §1: KHÔNG có kéo thẻ lên bản đồ (HTML5 drag / giữ lâu). Dải thẻ `touch-action: pan-x`; giữ lâu ở dải thẻ / bản đồ không
 *     mở menu trình duyệt; đang kéo chấm ⇒ chặn `touchmove` của trang (trang không trôi theo ngón).
 *   Lưu: tên + vị trí vào CSDL, không gửi gì xuống tủ. "Loại bỏ" = CÙNG bản nháp với "Loại khỏi bảng" của bảng node.
 *   Không chữ hướng dẫn.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { ArrowLeft, Crosshair, ListOrdered, Save, Search, X } from 'lucide-react';
import { BanDoTu } from '../BanDoTu';
import { ConfirmDialog, useToast } from '../ui';
import { errorMessage } from '../../lib/api';
import { useTree } from '../../lib/queries';
import { giayHai, useLuuXep, type TuCaiDat } from '../../lib/caiDatBanDau';
import {
  autoTen, goiLuuXep, khungVeTu, mauMacDinh, phanTichMau, posSau, soViTri, TEN_DEN_TOI_DA, tenSau, trangThaiXep, type NhapXep, type NodeXep, type ToaDo,
} from '../../lib/xepViTri';

const h4 = (n: number) => n.toString(16).toUpperCase().padStart(4, '0');
/* TK-45 §1: đang kéo chấm ⇒ trang không trôi theo ngón. Trình nghe KHÔNG passive — passive thì preventDefault vô hiệu. */
const chanTrangTroi = (e: TouchEvent) => { if (e.cancelable) e.preventDefault(); };
const batChanTroi = () => document.addEventListener('touchmove', chanTrangTroi, { passive: false });
const tatChanTroi = () => document.removeEventListener('touchmove', chanTrangTroi);
/** Chiều cao dải thẻ (phiếu: ~132 px; không vừa 810×1080 thì thu 112). */
export const CAO_DAI_THE = 132;

function TheDen({ h, n, chon, loai, onChon, onTen, onLoai }: {
  h: NodeXep; n: NhapXep | undefined; chon: boolean; loai: boolean;
  onChon: () => void; onTen: (t: string) => void; onLoai: () => void;
}) {
  const p = posSau(h, n);
  const cha = h.parentLuot ?? h.parentLuu ?? 0;
  const tt = trangThaiXep(h, n, loai ? new Set([h.addr]) : new Set());
  return (
    <div role="button" tabIndex={0} onClick={onChon} onKeyDown={(e) => { if (e.key === 'Enter') onChon(); }} aria-pressed={chon}
      data-the-den={h4(h.addr)} data-trang-thai={tt} data-loai-bo={loai ? '1' : undefined}
      className={`flex h-full w-[212px] shrink-0 cursor-pointer flex-col gap-[3px] rounded-[9px] border px-2.5 py-2 text-[11px] leading-tight ${chon ? 'border-nhan bg-nen-3' : 'border-vien bg-nen-1'} ${loai ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-1.5">
        <input value={tenSau(h, n) ?? ''} onChange={(e) => onTen(e.target.value)} onClick={(e) => e.stopPropagation()} placeholder="CS01" maxLength={TEN_DEN_TOI_DA}
          aria-label={`Tên đèn ${h4(h.addr)}`} data-o="ten-den" className="min-w-0 flex-1 rounded-[5px] border border-vien-manh bg-nen-lom px-1.5 py-[3px] text-[12.5px] font-semibold text-chu-1 placeholder:text-chu-3" />
        <span className={`shrink-0 rounded border px-1.5 py-px text-[10px] ${tt === 'da-luu' ? 'border-tot/40 text-tot' : 'border-canh/40 text-canh'}`} data-o="trang-thai">{tt === 'da-luu' ? 'Đã lưu' : 'Chưa lưu'}</span>
      </div>
      <div className="flex items-baseline gap-1.5 font-mono text-chu-1">
        <span data-o="id-goc">{h4(h.addr)}</span><span className="text-chu-3">→</span><span data-o="id-lien-ket">{cha === 0 ? '0000 · Host' : h4(cha)}</span>
        <span className="ml-auto text-chu-2" title="Tìm được lúc" data-o="tim-duoc">{h.thayMs === null ? '—' : `+${giayHai(h.thayMs)}`}</span>
      </div>
      <div className="truncate text-chu-2" title="gps_local — GPS đèn tự báo">GPS <b className="font-mono font-normal text-chu-1" data-o="gps-local">{soViTri(h.gps) ?? '—'}</b></div>
      <div className="truncate text-chu-2" title="gps_map — vị trí đặt trên bản đồ">Bản đồ <b className={`font-mono font-normal ${p ? 'text-chu-1' : 'text-chu-3'}`} data-o="gps-map">{soViTri(p) ?? 'Chưa đặt'}</b></div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onLoai(); }} data-o="loai-bo"
        className={`mt-auto self-start rounded-[5px] border px-2 py-[2px] text-[11px] ${loai ? 'border-vien-manh text-chu-1' : 'border-nguy/40 text-nguy'}`}>{loai ? 'Hoàn tác' : 'Loại bỏ'}</button>
    </div>
  );
}

/** Lớp trong MapContainer: chạm bản đồ ⇒ đặt đèn đang chọn; chấm kéo được; "Về tủ" / bay tới đèn. */
function LopXep({ ds, nhap, chon, tuToaDo, veTu, bay, onDat, onChon }: {
  ds: NodeXep[]; nhap: ReadonlyMap<number, NhapXep>; chon: number | null; tuToaDo: ToaDo | null;
  veTu: number; bay: { p: ToaDo; lan: number } | null;
  onDat: (addr: number, p: ToaDo) => void; onChon: (addr: number) => void;
}) {
  const map = useMap();
  useMapEvents({ click: (e) => { if (chon !== null) onDat(chon, { lat: e.latlng.lat, lng: e.latlng.lng }); } });
  /* "Về tủ": tâm = tủ, zoom vừa đủ tủ + các đèn đã đặt (khung đối xứng quanh tủ) */
  const dsRef = useRef({ ds, nhap }); dsRef.current = { ds, nhap };
  useEffect(() => {
    if (!tuToaDo || veTu === 0) return;
    const den = dsRef.current.ds.map((h) => posSau(h, dsRef.current.nhap.get(h.addr))).filter((p): p is ToaDo => !!p);
    const z = den.length ? Math.min(19, map.getBoundsZoom(L.latLngBounds(khungVeTu(tuToaDo, den)), false, L.point(48, 48))) : 18;
    map.setView([tuToaDo.lat, tuToaDo.lng], z, { animate: false });
  }, [veTu, tuToaDo, map]);
  useEffect(() => { if (bay) map.setView([bay.p.lat, bay.p.lng], Math.max(map.getZoom(), 18), { animate: false }); }, [bay, map]);
  useEffect(() => tatChanTroi, []);   // rời trang giữa lúc kéo ⇒ gỡ trình nghe
  return (
    <>
      {ds.map((h) => {
        const p = posSau(h, nhap.get(h.addr));
        if (!p) return null;
        const ten = tenSau(h, nhap.get(h.addr)) ?? h4(h.addr);
        const la = h.addr === chon;
        const icon = L.divIcon({
          className: 'cham-den-xep', iconSize: [28, 28], iconAnchor: [14, 14],
          html: `<span data-den-xep="${h4(h.addr)}" style="display:block;width:18px;height:18px;margin:5px;border-radius:999px;box-sizing:border-box;background:rgb(var(--nhan-sang));border:3px solid ${la ? 'rgb(var(--chu-1))' : 'rgb(var(--nen-0))'}"></span><span style="position:absolute;left:28px;top:5px;white-space:nowrap;font-size:11px;font-weight:600;color:rgb(var(--chu-1));text-shadow:0 0 3px rgb(var(--nen-0))">${ten.replace(/[<>&"]/g, '')}</span>`,
        });
        return (
          <Marker key={h.addr} position={[p.lat, p.lng]} icon={icon} draggable zIndexOffset={1000}
            eventHandlers={{ dragstart: batChanTroi, dragend: (e) => { tatChanTroi(); const ll = (e.target as L.Marker).getLatLng(); onDat(h.addr, { lat: ll.lat, lng: ll.lng }); }, click: () => onChon(h.addr) }} />
        );
      })}
    </>
  );
}

export function XepViTri({ tu, loai, doiLoai, nhap, setNhap, onVe }: {
  tu: TuCaiDat; loai: ReadonlySet<number>; doiLoai: (addr: number) => void;
  nhap: ReadonlyMap<number, NhapXep>; setNhap: (m: Map<number, NhapXep>) => void;
  /** "← Về bảng node" — trang hỏi trước nếu còn thay đổi */
  onVe: () => void;
}) {
  const toast = useToast();
  const tree = useTree();
  const luu = useLuuXep(tu.id);
  const [chon, setChon] = useState<number | null>(null);
  const [tim, setTim] = useState('');
  const [loi, setLoi] = useState<string | null>(null);
  const [veTu, setVeTu] = useState(0);
  const [bay, setBay] = useState<{ p: ToaDo; lan: number } | null>(null);
  /* TK-45 §5: ô mẫu tên (nút "CS01…") */
  const [moMau, setMoMau] = useState(false);
  const [mau, setMau] = useState('CS01');
  const [loiMau, setLoiMau] = useState<string | null>(null);
  const [hoiAutoHet, setHoiAutoHet] = useState(false);
  /* bản đồ CHỈ có tủ đang cài (tủ khác không vẽ) */
  const cay = useMemo(() => {
    const r = (tree.data ?? []).find((x) => x.cabinets.some((c) => c.id === tu.id));
    return r ? [{ ...r, cabinets: r.cabinets.filter((c) => c.id === tu.id) }] : [];
  }, [tree.data, tu.id]);
  useEffect(() => { const t = window.setTimeout(() => setVeTu((n) => n + 1), 300); return () => window.clearTimeout(t); }, [tu.id]);
  const k = tim.trim().toLowerCase();
  const ds = tu.bang;
  const hien = k ? ds.filter((h) => `${tenSau(h, nhap.get(h.addr)) ?? ''} ${h4(h.addr)}`.toLowerCase().includes(k)) : ds;
  const daDat = ds.filter((h) => posSau(h, nhap.get(h.addr))).length;
  const dat = (addr: number, p: Partial<NhapXep>) => { const m = new Map(nhap); m.set(addr, { ...m.get(addr), ...p }); setNhap(m); setLoi(null); };
  /* Auto (giữ tên sửa tay) / Auto lại tất cả — tên Auto KHÔNG mang cờ `tay` */
  const apAuto = (giuTay: boolean) => {
    const m = phanTichMau(mau);
    if (!m) { setLoiMau('Mẫu phải có số'); return; }
    const moi = new Map(nhap);
    for (const [a, t] of autoTen(ds, nhap, m, giuTay)) moi.set(a, { ...moi.get(a), ten: t, tay: false });
    setNhap(moi); setMoMau(false); setLoi(null);
  };
  const chonThe = (h: NodeXep) => {
    setChon(h.addr);
    const p = posSau(h, nhap.get(h.addr));
    if (p) setBay({ p, lan: Date.now() });
  };
  /* TK-45 §5: tên được trùng — không còn chặn trùng (chỉ ≤ 16 ký tự) */
  const luuNgay = () => {
    luu.mutate(goiLuuXep(nhap), {
      onSuccess: (r) => { setNhap(new Map()); setLoi(null); toast.success(`Đã lưu ${r.daLuu} đèn`); },
      onError: (e) => setLoi(errorMessage(e)),
    });
  };
  /* gọn để MỘT hàng vừa 810 px dọc (thanh bên 186 px còn lại 624 px): đệm 8 px, chữ 12 px, ô tìm co giãn */
  const NUT = 'inline-flex min-h-[44px] shrink-0 items-center gap-1 rounded-[7px] border border-vien-manh bg-nen-2 px-2 text-xs text-chu-1 hover:border-nhan-dam disabled:cursor-not-allowed disabled:opacity-45';
  return (
    <div className="flex h-[calc(100dvh-62px-env(safe-area-inset-top)-1.75rem-env(safe-area-inset-bottom))] min-h-0 flex-col" data-o="xep-vi-tri">
      {/* 1. thanh trên — một hàng */}
      {/* TK-45 §5: < 900 px được xuống hàng (ô Tìm ≥ 140 px, nút mẫu không cắt chữ); từ 900 px trở lên MỘT hàng */}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 whitespace-nowrap border-b border-vien px-2 py-2 min-[900px]:flex-nowrap" data-o="thanh-xep">
        <b className="min-w-0 max-w-[110px] shrink-0 truncate text-sm font-semibold text-chu-1" title={tu.name} data-o="ten-tu-xep">{tu.name}</b>
        <button type="button" onClick={onVe} className={NUT} data-o="ve-bang-node"><ArrowLeft className="h-3.5 w-3.5" />Về bảng node</button>
        <button type="button" onClick={() => setVeTu((n) => n + 1)} disabled={!tu.toaDo} className={NUT} data-o="ve-tu"><Crosshair className="h-3.5 w-3.5" />Về tủ</button>
        <label className="flex min-h-[44px] min-w-[140px] flex-1 items-center gap-1 rounded-[7px] border border-vien-manh bg-nen-lom px-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-chu-3" />
          <input value={tim} onChange={(e) => setTim(e.target.value)} placeholder="Tìm đèn" aria-label="Tìm đèn" data-o="tim-den" className="min-w-0 flex-1 bg-transparent text-[12.5px] text-chu-1 outline-none placeholder:text-chu-3" />
        </label>
        <span className="shrink-0 text-xs tabular-nums text-chu-2" data-o="dem-da-dat"><b className="text-chu-1">{daDat}</b>/{ds.length} đã đặt</span>
        <span className="relative shrink-0">
          <button type="button" onClick={() => { setMau(mauMacDinh(ds, nhap)); setLoiMau(null); setMoMau((v) => !v); }} aria-expanded={moMau} className={NUT} title="Đặt tên theo mẫu" data-o="danh-so"><ListOrdered className="h-3.5 w-3.5" />CS01…</button>
          {moMau && (
            <div className="absolute right-0 top-full z-[800] mt-1 flex w-[220px] flex-col gap-2 rounded-[9px] border border-vien-manh bg-nen-1 p-2.5 shadow-lg" data-o="o-mau-ten">
              <label className="flex items-center gap-2 text-xs text-chu-2">Mẫu
                <input value={mau} onChange={(e) => { setMau(e.target.value); setLoiMau(null); }} maxLength={TEN_DEN_TOI_DA} aria-label="Mẫu tên" data-o="mau-ten"
                  className="min-h-[36px] min-w-0 flex-1 rounded-[5px] border border-vien-manh bg-nen-lom px-2 text-[13px] font-semibold text-chu-1" /></label>
              {loiMau && <span className="text-xs text-nguy" data-o="loi-mau">{loiMau}</span>}
              <button type="button" onClick={() => apAuto(true)} className={`${NUT} justify-center border-nhan text-nhan`} data-o="auto">Auto</button>
              <button type="button" onClick={() => { if (phanTichMau(mau)) setHoiAutoHet(true); else setLoiMau('Mẫu phải có số'); }} className={`${NUT} justify-center`} data-o="auto-het">Auto lại tất cả</button>
            </div>
          )}
        </span>
        <span className="flex shrink-0 gap-1.5">
          <button type="button" onClick={() => { setNhap(new Map()); setLoi(null); }} disabled={nhap.size === 0} className={NUT} data-o="huy-xep"><X className="h-3.5 w-3.5" />Hủy</button>
          <button type="button" onClick={luuNgay} disabled={nhap.size === 0 || luu.isPending} data-o="luu-xep"
            className={`${NUT} border-nhan bg-nhan font-semibold text-nen-0 hover:bg-nhan-sang`}><Save className="h-3.5 w-3.5" />{luu.isPending ? 'Đang lưu…' : 'Lưu'}</button>
        </span>
      </div>
      {/* 2. dải thẻ đèn cuộn ngang */}
      <div className="flex shrink-0 gap-2 overflow-x-auto px-3 py-2" style={{ height: CAO_DAI_THE, touchAction: 'pan-x' }} data-o="dai-the-den"
        onContextMenu={(e) => e.preventDefault()}>
        {hien.map((h) => (
          <TheDen key={h.addr} h={h} n={nhap.get(h.addr)} chon={h.addr === chon} loai={loai.has(h.addr)}
            onChon={() => chonThe(h)} onTen={(t) => dat(h.addr, { ten: t, tay: true })} onLoai={() => doiLoai(h.addr)} />
        ))}
        {hien.length === 0 && <div className="self-center px-2 text-sm text-chu-3">{ds.length ? 'Không có đèn khớp' : 'Chưa có node'}</div>}
      </div>
      {/* 3. bản đồ phần còn lại */}
      <div className="relative min-h-0 flex-1 border-t border-vien" data-o="ban-do-xep" onContextMenu={(e) => e.preventDefault()}>
        <ConfirmDialog open={hoiAutoHet} danger={false} title="Auto lại tất cả" message="Bỏ hết tên hiện có, đặt lại theo mẫu."
          confirmText="Đặt lại" cancelText="Hủy" onCancel={() => setHoiAutoHet(false)} onConfirm={() => { setHoiAutoHet(false); apAuto(false); }} />
        {loi && <div className="absolute left-1/2 top-3 z-[700] -translate-x-1/2 rounded-[7px] border border-nguy/40 bg-nguy-nen px-3 py-2 text-[12.5px] text-nguy" data-o="loi-xep">{loi}</div>}
        <BanDoTu cay={cay} canhBao={undefined} caoLop="h-full" tranKhung nenToi hienSuCo={false}
          hopTrai={(
            <span className="flex items-center gap-1.5 text-chu-1" data-o="chu-giai-xep" data-so-den={ds.length} data-da-dat={daDat}>
              <span className="h-2.5 w-2.5 rounded-full bg-nhan-sang" aria-hidden="true" />Đèn {tu.name}
              <b className="tabular-nums">{daDat}</b><span className="text-chu-3">/ {ds.length}</span>
            </span>
          )}
          lopThem={<LopXep ds={ds} nhap={nhap} chon={chon} tuToaDo={tu.toaDo} veTu={veTu} bay={bay} onDat={(a, p) => dat(a, { pos: p })} onChon={(a) => setChon(a)} />} />
      </div>
    </div>
  );
}
